import { useQuery } from '@tanstack/react-query';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { useSeekAuth } from '@/contexts/SeekAuthContext';

export interface SeekInterviewSession {
  session_id: string;
  token: string;
  status: string;
  interview_type: string | null;
  overall_score: number | null;
  expires_at: string | null;
  session_created_at: string;
  completed_at: string | null;
  session_number: number;
  // Job info
  job_title: string;
  org_name: string | null;
  seek_app_id: string;
  hire_application_id: string;
  // F2F response status (from applications.f2f_details_json)
  f2f_response_status: string | null;
}

function getF2fResponseStatus(value: unknown): string | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const status = (value as Record<string, unknown>).f2f_response_status;
  return typeof status === 'string' ? status : null;
}

export function useSeekInterviewSessions() {
  const { user } = useSeekAuth();
  const uid = user?.id;

  const query = useQuery({
    queryKey: ['seek-interview-sessions', uid],
    queryFn: async () => {
      // Step 1: Get candidate's seek_applications with job info
      const { data: apps, error: appErr } = await seekSupabase
        .from('seek_applications')
        .select('id, hire_application_id, seek_job_catalog(title, org_name)')
        .eq('candidate_id', uid!)
        .not('hire_application_id', 'is', null);
      if (appErr) throw appErr;
      if (!apps || apps.length === 0) return [];

      // Step 2: Get interview_sessions for those hire_application_ids
      const hireAppIds = apps.flatMap((app) => app.hire_application_id ? [app.hire_application_id] : []);
      if (hireAppIds.length === 0) return [];

      const { data: sessions, error: sessErr } = await seekSupabase
        .from('interview_sessions')
        .select('id, application_id, token, status, interview_type, overall_score, expires_at, created_at, completed_at, session_number')
        .in('application_id', hireAppIds)
        .order('created_at', { ascending: false });
      if (sessErr) throw sessErr;
      if (!sessions || sessions.length === 0) return [];

      // Step 3: Fetch f2f_details_json for related applications
      const applicationIds = [...new Set(sessions.map((session) => session.application_id))];
      const f2fStatusMap: Record<string, string | null> = {};
      if (applicationIds.length > 0) {
        const { data: appDetails } = await seekSupabase
          .from('applications')
          .select('id, f2f_details_json')
          .in('id', applicationIds);
        if (appDetails) {
          for (const application of appDetails) {
            f2fStatusMap[application.id] = getF2fResponseStatus(application.f2f_details_json);
          }
        }
      }

      // Build lookup from hire_application_id → app info
      const appMap: Record<string, { seek_app_id: string; job_title: string; org_name: string | null }> = {};
      for (const a of apps) {
        if (a.hire_application_id) {
          appMap[a.hire_application_id] = {
            seek_app_id: a.id,
            job_title: a.seek_job_catalog?.title ?? 'Unknown Job',
            org_name: a.seek_job_catalog?.org_name ?? null,
          };
        }
      }

      return sessions.map((s): SeekInterviewSession => {
        const appInfo = appMap[s.application_id];
        return {
          session_id: s.id,
          token: s.token,
          status: s.status,
          interview_type: s.interview_type,
          overall_score: s.overall_score,
          expires_at: s.expires_at,
          session_created_at: s.created_at,
          completed_at: s.completed_at,
          session_number: s.session_number,
          job_title: appInfo?.job_title ?? 'Unknown Job',
          org_name: appInfo?.org_name ?? null,
          seek_app_id: appInfo?.seek_app_id ?? '',
          hire_application_id: s.application_id,
          f2f_response_status: f2fStatusMap[s.application_id] ?? null,
        };
      });
    },
    enabled: !!uid,
  });

  const sessions = query.data ?? [];
  const now = new Date();
  const activeSessions = sessions.filter(s =>
    s.status === 'invited' && (!s.expires_at || new Date(s.expires_at) > now)
    && !(s.session_number > 1 && s.f2f_response_status)
  );

  return {
    sessions,
    isLoading: query.isLoading,
    activeCount: activeSessions.length,
  };
}
